import { makeAutoObservable, runInAction } from 'mobx'
import { isAxiosError, isCancel } from 'axios'
import { RequestCancelAxios } from 'shared/api/lib'
import { logger } from 'shared/lib'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import {
  type AnalyticsResponse,
  type IConversionChartResponse,
  type IConversionStatisticResponse,
  type ProcessedStatistic,
  ConversionsAnalyticsApi,
} from 'entities/Analytics'
import { ConversionsTableStore } from 'pages/analytics/pages/conversions/ui/ConversionsTable'
import { calculateTrend, computeHasStatistic } from 'pages/analytics/lib'
import { AnalyticsFiltersStore } from 'pages/analytics/store/analyticsFiltersStore'
import { ExportAnalyticsModalStore } from 'pages/analytics/ui/ExportAnalyticsModal'

export class ConversionsAnalyticsListStore {
  private readonly _analyticsFiltersStore: AnalyticsFiltersStore
  private readonly _exportAnalyticsModalStore: ExportAnalyticsModalStore
  private readonly _conversionsTableStore: ConversionsTableStore
  private readonly _requestCancelAxios = new RequestCancelAxios()

  private _error: string | null = null
  private _pageLoading = false
  private _rawStatistic: IConversionStatisticResponse[] = []
  private _hasRetriedAfterTimeout = false
  private _isRetrying = false
  private _requestGeneration = 0

  constructor(entities: IEntitiesStore) {
    this._analyticsFiltersStore = new AnalyticsFiltersStore(entities, {
      onFiltersChange: () => this._startFetch(),
      additionalFilterTypes: [],
      breakdownFilterTypes: [],
    })
    this._exportAnalyticsModalStore = new ExportAnalyticsModalStore(
      ConversionsAnalyticsApi.exportAnalytics
    )
    this._conversionsTableStore = new ConversionsTableStore(
      () => this._analyticsFiltersStore.analyticsFiltersParams
    )

    makeAutoObservable(this)
  }

  init = () => {
    this._analyticsFiltersStore.initReactions()
    this._startFetch()
    this._conversionsTableStore.init()
  }

  openExportModal = () => {
    const { analyticsFiltersParams } = this._analyticsFiltersStore
    this._exportAnalyticsModalStore.onOpenExport(analyticsFiltersParams)
  }

  private _startFetch = () => {
    this._requestGeneration += 1
    this._fetchAnalytics()
  }

  private _fetchAnalytics = async () => {
    const generation = this._requestGeneration

    runInAction(() => {
      this._pageLoading = true
    })

    try {
      this._error = null
      const { analyticsFiltersParams } = this._analyticsFiltersStore

      this._requestCancelAxios.init()

      await ConversionsAnalyticsApi.preloadAnalytics(analyticsFiltersParams, {
        cancelToken: this._requestCancelAxios.token,
      })

      const { data } = await ConversionsAnalyticsApi.getAnalytics(analyticsFiltersParams, {
        cancelToken: this._requestCancelAxios.token,
      })

      if (generation !== this._requestGeneration) return

      this.setRawData(data)
      runInAction(() => {
        this._hasRetriedAfterTimeout = false
      })
    } catch (error) {
      if (isCancel(error)) {
        runInAction(() => {
          this._hasRetriedAfterTimeout = false
        })
        return
      }

      if (generation !== this._requestGeneration) return

      const isTimeout =
        isAxiosError(error) && (error.response?.status === 408 || error.response?.status === 504)

      if (isTimeout && !this._hasRetriedAfterTimeout) {
        logger.warn(
          'ANALYTICS: conversions Request timed out, likely due to large data volume. Retrying once.'
        )
        runInAction(() => {
          this._hasRetriedAfterTimeout = true
          this._isRetrying = true
        })
        this._fetchAnalytics()
        return
      }

      runInAction(() => {
        this._hasRetriedAfterTimeout = false
        this._error = 'Failed to load analytics data'
      })
    } finally {
      if (generation === this._requestGeneration) {
        runInAction(() => {
          if (!this._isRetrying) {
            this._pageLoading = false
          }
          this._isRetrying = false
        })
      }
    }
  }

  setRawData = (
    response: AnalyticsResponse<IConversionStatisticResponse, IConversionChartResponse>
  ) => {
    this._rawStatistic = response.statistic
  }

  get processedStatistic(): ProcessedStatistic[] {
    if (!this._rawStatistic) {
      return []
    }
    return this._rawStatistic.map((item) => {
      const { key, value, prevValue, label } = item
      const trend = calculateTrend(value, prevValue)

      return {
        key: key,
        label: label || key,
        value: value,
        prevValue: prevValue,
        trend,
      }
    })
  }

  getStatisticByKey = (key: string): ProcessedStatistic | undefined => {
    return this.processedStatistic.find((item) => item.key === key)
  }

  get hasStatistic() {
    return computeHasStatistic(this._rawStatistic)
  }

  get calendarFilterStore() {
    return this._analyticsFiltersStore.calendarFilterStore
  }

  get conversionsTableStore() {
    return this._conversionsTableStore
  }

  get pageLoading() {
    return this._pageLoading
  }

  get error() {
    return this._error
  }

  dispose = () => {
    this._rawStatistic = []
    this._analyticsFiltersStore.dispose()
    this._requestCancelAxios.reset()
    this._exportAnalyticsModalStore.dispose()
    this._conversionsTableStore.dispose()
  }
}
